import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { SkillAndCertification } from 'types/interfaces/';
import VisibilityWrapper from 'utils/wrappers/VisibilityWrapper';

import { AttainmentService } from 'components/services';
import { AttainmentTemplate } from 'constants/AttainmentTemplate';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';
import SkillsAndCertificationsCard from 'ui/SkillsAndCertificationsCard';

import { useDebounce } from '../hooks';
import { ruLocale } from '../lang/ruLocale';


export default function SkillsAndCertifications() {
  const toast = useRef<Toast>(null);
  const attainmentCardRef = useRef<null | { handleCancel: () => void }>(null);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const [records, setRecords] = useState<SkillAndCertification[]>([]);
  const [selectedSkill, setSelectedSkill] =
    useState<SkillAndCertification>(AttainmentTemplate);
  const [cardVisible, setCardVisible] = useState(false);
  const [filters, setFilters] = useState<Record<string, DataTableFilterMetaData>>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    shortName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);

  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [multiSortMeta, setMultiSortMeta] = useState<DataTableSortMeta[] | null>(null);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  const loadData = useCallback(() => {
    const sort = multiSortMeta?.[0]; 
    const payload = JSON.stringify([
      { field: 'name', value: filters.name?.value, operator: filters.name?.matchMode },
      { field: 'shortName', value: filters.shortName?.value, operator: filters.shortName?.matchMode },
      { field: 'description', value: filters.description?.value, operator: filters.description?.matchMode },
      { field: 'global', value: filters.global?.value, operator: filters.global?.matchMode }
    ]);

    const service = AttainmentService.getAttainments({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    });

    service.then(res => {
      if (res.data.isSuccess) {
        setRecords(res.data.value.itemsList);
        setTotalCount(res.data.value.total || 0);
      }
    });
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    debouncedLoadData();
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);
    setFilters(e.filters as Record<string, DataTableFilterMetaData>);
  }, []);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPageNumber(1);
    setFirst(0);
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], value }
    }));
  };

  const handleAdd = (skill: SkillAndCertification) =>
    AttainmentService.createAttainment(skill).then(res => {
      loadData();
      return res;
    });

  const handleUpdate = (id: number, skill: SkillAndCertification) =>
    AttainmentService.updateAttainment(id, skill).then(res => {
      loadData();
      return res;
    });

  const getSelectedAttainment = useCallback(async (skill: SkillAndCertification) => {
    const serviceResult = await AttainmentService.getAttainmentById(skill.id);
    if (serviceResult.data) {
      setSelectedSkill(serviceResult.data.value);
      setCardVisible(true);
    }
  }, []);

  const handleRowDoubleClick = (e: any) => {
    getSelectedAttainment(e.data as SkillAndCertification);
  };


  const addButtonHandle = () => {
    setSelectedSkill(AttainmentTemplate);
    setCardVisible(true);
  };

   const onHideAttainmentCard = () => {
    if (attainmentCardRef.current !== null) {
      attainmentCardRef.current.handleCancel();
    }
  };

  const editButtonHandle = () => {
    if (toast.current && selectedSkill.id == 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите навык для редактирования',
        life: 3000
      });
    }
    else setCardVisible(true);
  };

  const deleteButtonHandle = () => {
    if (selectedSkill.id !== 0) {
      setDeleteDialogVisible(true);
    } else {
      toast.current?.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите навык для удаления',
        life: 3000
      });
    }
  };

  const header = (  
    <TablePageHeader
      tableName="attainments"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )


  const closeDialog = () => {
    setCardVisible(false);
  };

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    const rect = el.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement ? paginatorElement.offsetHeight : 0;
    const tableOffset = tableElement instanceof HTMLElement ? offset(tableElement).top : 0;

    return `calc(100vh - ${tableOffset + paginatorHeight}px)`;
  }, [tableElement]);


  return (
    <div className="wrapper wrapper--main">
      <Toast ref={toast} />

      <Dialog
        header="Редактирование навыка"
        visible={cardVisible}
        style={{ width: '600px', minHeight: '426px' }}
        maximizable
        modal
        onHide={onHideAttainmentCard}
      >
        <VisibilityWrapper code="attainment-card__roles">
          <SkillsAndCertificationsCard
            skillsAndCertificationProps={selectedSkill}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            onUpdate={handleUpdate}
            onDelete={id => {
              AttainmentService.deleteAttainment(Number(id)).then(() => loadData());
            }}
            ref={attainmentCardRef}
          />
        </VisibilityWrapper>
      </Dialog>
      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={() => {AttainmentService.deleteAttainment(selectedSkill.id).then(() => loadData())}}
        entityType="навык"
        entityName={selectedSkill?.name}
      />
      <DataTable
        size="small"
        value={records}
        lazy
        paginator
        first={first}
        rows={pageSize}
        paginatorLeft={' '}
        paginatorRight={`Всего: ${records.length > 0 ? totalCount : 0}`}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPage={onPageChanged}
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        sortMode="multiple"
        removableSort
        selectionMode="single"
        selection={selectedSkill}
        onSelectionChange={e => setSelectedSkill(e.value as SkillAndCertification)}
        header={header}
        filters={filters}
        onFilter={onFiltersChange}
        onRowDoubleClick={handleRowDoubleClick}
        filterDisplay="row"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        scrollable
        scrollHeight={scrollHeight}
      >
        <Column
          field="name"
          header="Название навыка"
          sortable
          filter
          filterElement={
            <InputText
              value={filters.name?.value || ''}
              onChange={handleInputChange('name')}
              placeholder="Поиск по названию"
            />
          }
        />
        <Column
          field="shortName"
          header="Короткое название"
          sortable
          filter
          filterElement={
            <InputText
              value={filters.shortName?.value || ''}
              onChange={handleInputChange('shortName')}
              placeholder="Поиск по короткому названию"
            />
          }
        />
        <Column
          field="description"
          header="Описание"
          sortable
          filter
          filterElement={
            <InputText
              value={filters.description?.value || ''}
              onChange={handleInputChange('description')}
              placeholder="Поиск по описанию"
            />
          }
        />
      </DataTable>
    </div>
  );
}
